import { AuctionStatus } from '@/types/LiveAuctionState';
import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { fetchCategoryRelatedSearchTiles } from '../search/categoryRelatedSearches/categoryRelatedSearches.actions';
import { fetchDataForEndedItemPage } from '@/redux/modules/itemPage/endedItemPage.actions';
import { FetchDataForItemPageParams } from './itemPage.types';
import { fetchDataForLiveItemPage } from '@/redux/modules/itemPage/liveItemPage.actions';
import { fetchDataForUpcomingItemPage } from '@/redux/modules/itemPage/upcomingItemPage.actions';
import { fetchItemDetailsIfNeeded } from '../itemDetail';
import { fetchItemFacetsIfNeeded, getItemFacets } from '../itemFacets';
import { fetchItemSummaries } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchSavedItemCountsIfNeeded } from '../savedItemCount';
import { fetchTopSellersIfNeeded } from '../topRatedSellers';
import { getAreUrlsEqual, getItemUrlFromSlug, getPriceResultsUrl, isPriceResultsPage } from '@/utils/urls';
import { getCarousels } from '../itemCarousel/itemCarousel.selectors';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getIsBot } from '../analytics';
import {
    getItemModelBuyNowAvailable,
    getItemModelCatalogStatus,
    getItemModelSlug,
    getItemModelSlugWithLocation,
    getItemModelTitle,
} from '../item/summary/itemSummary.selectors';
import { isClientSide } from '@/utils/amplitude/analytics/core';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { loadItemCarousels } from '../itemCarousel/itemCarousel.actions';

export const fetchDataForItemPage = createAsyncThunk<Promise<unknown>, FetchDataForItemPageParams>(
    'la/domain/itemPage/fetchDataForItemPage',
    async (
        { itemId, location, redirect, referrer = '', useNewSimilarItems, useScoutCategories },
        { dispatch, getState }
    ) => {
        const isBot = getIsBot(getState());

        await Promise.all([
            dispatch(
                fetchItemSummaries({
                    identifier: 'item-page-main',
                    itemIds: [itemId],
                    similarItems: true,
                })
            ),
            dispatch(fetchItemFacetsIfNeeded([itemId])),
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchSavedItemCountsIfNeeded([itemId])),
            dispatch(fetchTopSellersIfNeeded()),
        ]);

        // Fetch category related searches after itemFacets are loaded
        const facets = getItemFacets(getState(), itemId);
        const categoryIds = getFacetIds(facets);
        if (categoryIds.length) {
            dispatch(fetchCategoryRelatedSearchTiles({ categoryIds }));
        }

        // Fetch carousel data and item summaries for SSR
        // Check if we're on server-side (typeof window === 'undefined') to ensure SSR works
        const isClient = isClientSide();
        if (!isClient || isBot) {
            // Use loadItemCarousels to force fresh carousel data for SSR
            await dispatch(loadItemCarousels({ itemId, useNewSimilarItems, useScoutCategories }));

            // Now fetch item summaries for all carousel items
            const carouselState = getState();
            const carousels = getCarousels(carouselState, itemId);
            const carouselItemIds: number[] = [];

            carousels.forEach((carousel) => {
                if (carousel.itemIds && carousel.itemIds.length > 0) {
                    carouselItemIds.push(...carousel.itemIds);
                }
            });

            if (carouselItemIds.length > 0) {
                await dispatch(
                    fetchItemSummaries({
                        identifier: 'item-page-carousel-items-ssr',
                        itemIds: carouselItemIds,
                    })
                );
            }
        }

        const state = getState();
        const isLoggedIn = isUserLoggedIn(state);
        const title = getItemModelTitle(state, itemId);
        const catalogStatus = getItemModelCatalogStatus(state, itemId);
        const buyNow = getItemModelBuyNowAvailable(state, itemId);
        const slug = getItemModelSlug(state, itemId);
        const slugWithLocation = getItemModelSlugWithLocation(state, itemId);

        // These redirects we're only going to do on the server so only when window is undefined.
        if (!isClient) {
            const { pathname, search } = location;
            const itemUrl = getItemUrlFromSlug(itemId, slugWithLocation);

            // If you click on a link from liveauctioneers.com to an item page, we don't want to redirect
            const liveAuctioneersReferrer = referrer.includes('liveauctioneers.com');

            // Handle the price-results redirect
            if (catalogStatus === AuctionStatus.Done && slug && !isLoggedIn && !liveAuctioneersReferrer && !buyNow) {
                return redirect(getPriceResultsUrl(slug), true);
            }

            // Handle the bad url redirect where the itemId is good but the item title is not
            if (!getAreUrlsEqual(pathname, itemUrl) && !isPriceResultsPage(location.pathname) && title) {
                // goodUrl is found, return a permanent redirect with the better url
                if (search) {
                    return redirect(`${itemUrl}${search}`, true);
                }
                return redirect(itemUrl, true);
            }
        }

        if (catalogStatus === AuctionStatus.Done) {
            return dispatch(fetchDataForEndedItemPage({ itemId }));
        } else if (catalogStatus === 'online') {
            return dispatch(fetchDataForUpcomingItemPage({ itemId }));
        } /* the other case is catalogStatus === 'live' which is handled here */ else {
            return dispatch(fetchDataForLiveItemPage({ itemId, useNewSimilarItems, useScoutCategories }));
        }
    }
);
